// react
import styled from 'styled-components';
import { useMemo } from 'react';

import { Card, Container, Icon } from '@/components/atoms';
import { Icons } from '@/components/atoms/Icon';
import { completionStepsArray } from './fixtures';

// types
export type IconColor = 'orange' | 'blue' | 'success' | 'primary';

export interface SuccessContentFixture {
  icon: Icons;
  text: string;
  iconColor: IconColor;
}

export const SuccessContent = ({
  fixtures,
  header,
  cardHeader,
  isComplete,
}: SuccessContentProps) => {
  const contentFixtures = useMemo(
    () => (isComplete ? fixtures : completionStepsArray),
    [isComplete],
  );

  return (
    <Container size='md'>
      <Wrapper>
        <WrapperHeader>
          <ColoredIcon
            icon='clipboard-check'
            fontSize={50}
            iconColor={'success'}
          />
          <Header>
            {header} {isComplete ? 'Ready For Review' : 'Needs Completing'}
          </Header>
        </WrapperHeader>
      </Wrapper>
      <ContentCard>
        <ContentHeader>{cardHeader}</ContentHeader>
        <table>
          {contentFixtures.map(({ icon, text, iconColor }, index) => (
            <tr key={index}>
              {isComplete ? (
                <IconCell>
                  <ColoredIcon
                    icon={icon as Icons}
                    iconColor={iconColor}
                    fontSize={30}
                  />
                </IconCell>
              ) : (
                <TextCell>{index + 1}.</TextCell>
              )}
              <TextCell>{text}</TextCell>
            </tr>
          ))}
        </table>
      </ContentCard>
    </Container>
  );
};

const WrapperHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  margin-top: ${(props) => props.theme.spacing[20]};
  margin-bottom: ${(props) => props.theme.spacing[16]};
`;

const ProgressHeader = styled.div`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.heading['3xl']};
  text-align: center;
`;

const Header = styled(ProgressHeader)`
  color: ${(props) => props.theme.color.neutral[100]};
`;

const ContentHeader = styled(ProgressHeader)`
  text-align: left;
  color: ${(props) => props.theme.color.neutral[90]};
`;

const ContentCard = styled(Card)`
  padding: ${(props) => props.theme.spacing[32]};
  margin-bottom: ${(props) => props.theme.spacing[24]};
`;

const ColoredIcon = styled(Icon)<{
  iconColor: IconColor;
}>`
  color: ${(props) =>
    props.iconColor === 'orange'
      ? props.theme.color.primary[30]
      : props.iconColor === 'blue'
        ? props.theme.color.actionPlanCards.blue.primary
        : props.iconColor === 'success'
          ? props.theme.color.success[40]
          : props.theme.color.primary[70]};
`;

const TextCell = styled.td`
  font: ${(props) => props.theme.typography.body.md.regular};
  padding: ${(props) => props.theme.spacing[12]};
`;

const IconCell = styled.td`
  padding-right: ${(props) => props.theme.spacing[12]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 100%;
  gap: ${(props) => props.theme.spacing[16]};
  margin: ${(props) => props.theme.spacing[12]};
`;

interface SuccessContentProps {
  fixtures: SuccessContentFixture[];
  header: string;
  cardHeader: string;
  isComplete: boolean;
}

SuccessContent.displayName = 'SuccessContent';

export default SuccessContent;
